import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Row, Switch, Table, Tooltip, notification } from "antd";

import { useAppDispatch, useAppSelector } from "../store";
import {
  AppUser,
  addAppUser,
  applyAssignable,
  fetchAppUsers,
  searchDirectoryUsers,
  setAppUserAssignable,
  upsertAppUser,
} from "../store/appUsersSlice";
import { clearAssignableUsersCache } from "../store/userSlice";
import DebounceSelect from "./DebounceSelect";

const SearchStyle = {
  maxWidth: "20%",
  display: "flex",
  flex: 1,
};

const { Search } = Input;

const formatSeen = (value: string | null) => {
  if (!value) {
    return "Never signed in";
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? "Never signed in" : date.toLocaleDateString();
};

/**
 * Admin control over who the Assigned To picker on the document page offers (FOC-5953).
 *
 * Switching somebody off only takes them out of that picker. The list page keeps showing them
 * under Assigned To for as long as they hold documents, so work already given to them stays
 * visible and filterable.
 */
function AssignableUsers() {
  const dispatch = useAppDispatch();
  const { data, loading } = useAppSelector((state) => state.appUsers);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [picked, setPicked] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchAppUsers());
  }, [dispatch]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return data;
    }
    return data.filter((u) => `${u.displayName ?? ""} ${u.mail ?? ""}`.toLowerCase().includes(term));
  }, [data, search]);

  const onToggle = (user: AppUser, isAssignable: boolean) => {
    // Flip now, put it back if the server disagrees.
    dispatch(applyAssignable({ id: user.id, isAssignable }));
    setSaving(user.id);

    setAppUserAssignable(user.id, isAssignable)
      .then(() => {
        // This admin's own picker would otherwise serve the pre-change list for up to 5 minutes.
        clearAssignableUsersCache();
      })
      .catch(() => {
        dispatch(applyAssignable({ id: user.id, isAssignable: !isAssignable }));
        notification.error({
          message: "Assignable Users",
          description: `Could not update ${user.displayName}. Please try again.`,
        });
      })
      .finally(() => setSaving(null));
  };

  const hideModal = () => {
    setIsModalOpen(false);
    setPicked(null);
  };

  const onAdd = () => {
    if (!picked) {
      hideModal();
      return;
    }

    setIsAdding(true);
    addAppUser({ id: picked.value, displayName: picked.label, mail: picked.mail })
      .then((user) => {
        dispatch(upsertAppUser(user));
        clearAssignableUsersCache();
        hideModal();
        notification.open({
          message: "Assignable Users",
          description: `${user.displayName} can now be assigned documents.`,
        });
      })
      .catch(() => {
        notification.error({
          message: "Assignable Users",
          description: "Could not add that user. Please try again.",
        });
      })
      .finally(() => setIsAdding(false));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "displayName",
      sorter: (a: AppUser, b: AppUser) => (a.displayName ?? "").localeCompare(b.displayName ?? ""),
    },
    {
      title: "Email",
      dataIndex: "mail",
    },
    {
      title: "Last signed in",
      dataIndex: "lastSeenAt",
      render: (value: string | null) => formatSeen(value),
    },
    {
      title: "In Assigned To list",
      dataIndex: "isAssignable",
      width: 180,
      render: (isAssignable: boolean, user: AppUser) => (
        <Switch
          checked={isAssignable}
          loading={saving === user.id}
          onChange={(checked) => onToggle(user, checked)}
          aria-label={`Assignable: ${user.displayName}`}
        />
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" style={{ marginTop: 20, marginBottom: 20 }}>
        <Search
          style={SearchStyle}
          onChange={({ target }) => setSearch(target.value)}
          allowClear
          placeholder="Search"
          size="middle"
          value={search}
        />
        <Tooltip title="Add somebody who has not signed in to OCR yet">
          <Button className="text-end" onClick={() => setIsModalOpen(true)}>
            Add User
          </Button>
        </Tooltip>
      </Row>
      <p style={{ color: "rgba(0, 0, 0, 0.45)" }}>
        These are the people the Assigned To list offers on a document. Switching somebody off does not
        change the Assigned To filter on the dashboard — documents already assigned to them stay there.
      </p>
      <Table
        className="custom-table"
        style={{ width: "100%" }}
        loading={loading}
        rowKey={(u: AppUser) => u.id}
        columns={columns}
        dataSource={visible}
        pagination={{ pageSize: 20, showSizeChanger: false }}
      />
      <Modal
        title="Add a user"
        open={isModalOpen}
        onOk={onAdd}
        onCancel={hideModal}
        cancelText="Cancel"
        okText="Add"
        okButtonProps={{ disabled: !picked }}
        confirmLoading={isAdding}
      >
        <p>
          Search everyone in the directory. Adding somebody puts them in the Assigned To list straight
          away, whether or not they have ever opened OCR.
        </p>
        <DebounceSelect
          id="directory-user-select"
          selected={picked}
          style={{ width: "100%" }}
          placeholder="Search by name"
          fetchOptions={searchDirectoryUsers}
          onChange={(value, option) => setPicked((option as any) ?? (value as any) ?? null)}
        />
      </Modal>
    </>
  );
}

export default AssignableUsers;
